/**
 * The workspace views shown in place of the project's page (utils/workspaceModel.js), one place each:
 * - Changes: By person (changes grouped by person and agent run, with revert: ChangesPanel), Timeline (the
 *   checkpoints, a commit's diff, rewind: HistoryPanel) or Versions (the app's named versions, saved by hand or on
 *   Publish, to go back to: VersionsPanel), the view last used remembered;
 * - Activity: the whole timeline, people and AI agents (ActivityPanel); an agent run opens Changes on it;
 * - Sessions: the project's chats and AI tool sessions (ProjectSessions);
 * - API: try the project's services' APIs, signed in as an app user, a visitor or with a token (components/api/ApiPanel);
 *   its Data link opens Data;
 * - MCP: the MCP servers the project's services expose, tried like MCP Inspector: their tools, resources and prompts,
 *   a tool called from a form made from its schema (components/mcp/McpPanel);
 * - Data: the project's datastores, browsable and editable (components/data/DataPanel); the ones not used yet can be
 *   added by asking the AI (`onAskAI`, prefilled in the project chat); `focus: { store, object? }` opens one store,
 *   `focus: { view: 'restore', point? }` its Restore points;
 * - Traces: who calls whom from the project's requests, and one request's timing (components/traces/TracesPanel); its
 *   links open Data on a store (`onChange`), the code at a line, or the chat (`onAskAI`);
 * - Quality: tests and coverage, lint, secrets, security, vulnerabilities, dependencies, the SBOM, the release gate and
 *   the compliance assessments (components/quality/QualityPanel); "Fix with AI" asks the chat (`onAskAI`), its file
 *   links open the code, an assessment's evidence opens Data or Traces (`onChange`);
 * - Configuration: a service's variables and secrets (write-only) (components/appconfig/ConfigPanel), `focus: { service }`
 *   opens one service, and a line to Jobs; Jobs (Operate): every service's scheduled jobs (components/appconfig/JobsPanel),
 *   the old `{ tab: 'config', focus: { section: 'jobs' } }` landing there (workspaceModel normalizeView);
 * - FinOps (Operate): what the project costs in Flow, for AI and in production, every estimate labelled
 *   (components/finops/FinOpsPanel); External Services (id `mail`): Mail, the emails the app sent and the Outbox, then
 *   CRMs, messaging systems and APIs coming soon (components/appconfig/ExternalServicesPanel);
 * - Terminal: shells in the sandbox, the Code view dock's TerminalPanel full height, several at once as sub-tabs
 *   (components/terminal/TerminalTabView); offered only to the project's owner and editors (hooks/useTerminalTab.js).
 *   Once opened it stays mounted, hidden, while another tab is shown (or none: Home, Code, Preview), so its shells live
 *   on until the person leaves the project or closes them; Canvas keeps this component mounted for that (`view` null).
 * - Files: the project's Brain, inputs, results and screenshots in one table (components/files/FilesPanel); `focus: { path }`
 *   shows one file in the tab (its path the title's last crumb, Files a link back to the table);
 * - Git: each service's repository, commit, tag, branch, push and pull, clone from Flow (components/git/GitTab);
 *   `readOnly` (a viewer): the overview only.
 * `view` `{ tab, sub, focus }`; `onChange(view)` moves between them. Changes, Activity and Sessions are the three views
 * of History (`historyMerged`, design "Flow Project Tabs"): Changes N · Activity · Sessions on top, the last one used
 * remembered (History opens on it); split into three tabs, the row is not shown.
 */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import HistoryPanel from '../HistoryPanel';
import VersionsPanel from '../VersionsPanel';
import ChangesPanel from '../collab/ChangesPanel';
import ActivityPanel from '../collab/ActivityPanel';
import ProjectSessions from './ProjectSessions';
import DataPanel from '../data/DataPanel';
import ApiPanel from '../api/ApiPanel';
import McpPanel from '../mcp/McpPanel';
import TracesPanel from '../traces/TracesPanel';
import QualityPanel from '../quality/QualityPanel';
import ConfigPanel from '../appconfig/ConfigPanel';
import JobsPanel from '../appconfig/JobsPanel';
import FinOpsPanel from '../finops/FinOpsPanel';
import ExternalServicesPanel from '../appconfig/ExternalServicesPanel';
import TerminalTabView from '../terminal/TerminalTabView';
import GitTab from '../git/GitTab';
import AppsPanel from '../apps/AppsPanel';
import FilesPanel from '../files/FilesPanel';
import { CHANGES_SUBS, normalizeView, rememberChangesSub, viewFromHistory } from '../../utils/workspaceModel';
import { HISTORY_IDS, HISTORY_SUBS, rememberHistorySub, tabHeading } from '../../utils/projectTabsModel';
import { usePlanTab } from '../../hooks/usePlanFeatures';
import '../collab/Collab.css';
import './Workspace.css';

/** The project's Home, from a tab page's title: the top bar (UnifiedTopBar) selects it. */
const openHome = () => window.dispatchEvent(new CustomEvent('flow:open-project-tab', { detail: { tab: 'dashboard' } }));
/** A step of the work (Build, Verify…), from a tab page's title: More opens on that step's tabs (WorkspaceTabs). */
const openGroup = (group) => window.dispatchEvent(new CustomEvent('flow:open-tab-group', { detail: { group } }));

/**
 * The title on top of a tab (design "Flow Project Redesign": "<Project> / <Step> / <Tab>" and what the tab is for):
 * on every workspace view, the Terminal, and the project's pages Code, Preview and Logs (Canvas). `compact`: the line
 * only, for a page that fills the height (Code, Preview, Logs). The project's name is a link to its Home and the step
 * opens its tabs (More), both real buttons; `onOpenProject` / `onOpenGroup` replace the default events.
 * A tab's page of its own (Quality › API contract: `sub`) is the last crumb, the tab's name before it a link back to
 * the tab's first page and, for a page opened from another one (Quality › Tests and coverage › a run), the pages in
 * between as links too (`sub.trail`); a long last crumb is cut with an ellipsis, whole in its tooltip. On the tab's
 * first page `crumbRef` (a callback ref) gets a slot after the tab's name: the tab puts a crumb of its own there
 * (Quality's section picker, "Quality / Overview ▾"). `toolsRef` (a callback ref) gets the line's right end: the tab
 * puts its own pickers and actions there (Quality's "Checks of", Run all checks), wrapping under the line on a phone.
 */
export function WorkspaceTitle({
  tab, projectName = null, compact = false, onOpenProject = null, onOpenGroup = null, sub = null, toolsRef = null, crumbRef = null,
}) {
  const heading = tabHeading(tab);
  if (!heading) { return null; }
  return (
    <header className={`workspace-view-title${compact ? ' is-compact' : ''}`} data-testid="workspace-view-title">
      <div className="workspace-view-title-line">
        <nav aria-label="Breadcrumb" className="workspace-view-crumb">
          {projectName && (
            <button
              className="workspace-view-project workspace-view-crumb-link"
              data-testid="workspace-view-project"
              onClick={() => (onOpenProject || openHome)()}
              title={`${projectName}: open the project's Home`}
              type="button"
            >
              {projectName}
            </button>
          )}
          {projectName && <span aria-hidden="true" className="workspace-view-sep">/</span>}
          {heading.groupLabel && (
            <button
              className={`workspace-view-group workspace-view-crumb-link is-${heading.group}`}
              data-testid="workspace-view-group"
              onClick={() => (onOpenGroup || openGroup)(heading.group)}
              title={`${heading.groupLabel}: show its tabs`}
              type="button"
            >
              {heading.groupLabel}
            </button>
          )}
          {heading.groupLabel && <span aria-hidden="true" className="workspace-view-sep">/</span>}
          {sub ? (
            <>
              <button
                className="workspace-view-tab workspace-view-crumb-link"
                data-testid="workspace-view-tab"
                onClick={sub.onUp}
                title={`Back to ${heading.title}`}
                type="button"
              >
                {heading.title}
              </button>
              <span aria-hidden="true" className="workspace-view-sep">/</span>
              {(sub.trail || []).map((step, index) => (
                <React.Fragment key={`${index}:${step.label}`}>
                  <button
                    className="workspace-view-tab workspace-view-crumb-link"
                    data-testid="workspace-view-trail"
                    onClick={step.onOpen}
                    title={`Back to ${step.label}`}
                    type="button"
                  >
                    {step.label}
                  </button>
                  <span aria-hidden="true" className="workspace-view-sep">/</span>
                </React.Fragment>
              ))}
              <h2 aria-current="page" className={`workspace-view-last${sub.mono ? ' is-mono' : ''}`} title={sub.hint || sub.title}>{sub.title}</h2>
            </>
          ) : (
            <>
              <h2 aria-current="page">{heading.title}</h2>
              {crumbRef && <span className="workspace-view-crumb-slot" data-testid="workspace-view-crumb-slot" ref={crumbRef} />}
            </>
          )}
        </nav>
        {toolsRef && <div className="workspace-view-title-tools" data-testid="workspace-view-title-tools" ref={toolsRef} />}
      </div>
      {!compact && <p>{heading.desc}</p>}
    </header>
  );
}

WorkspaceTitle.propTypes = {
  compact: PropTypes.bool,
  // (element) => void: a callback ref for a slot after the tab's name on its first page (a crumb of the tab's own).
  crumbRef: PropTypes.func,
  // () => void: the project's name clicked (default: `flow:open-project-tab` Home).
  onOpenProject: PropTypes.func,
  // (group) => void: the step clicked (default: `flow:open-tab-group`).
  onOpenGroup: PropTypes.func,
  projectName: PropTypes.string,
  // A page of the tab's own, the last crumb: `{ title, hint?, mono?, onUp, trail? }` (onUp: back to the tab's first
  // page; trail: the pages it was opened from, `[{ label, onOpen }]`, links between the tab's name and the title).
  sub: PropTypes.shape({
    hint: PropTypes.string,
    mono: PropTypes.bool,
    onUp: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    trail: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string.isRequired, onOpen: PropTypes.func.isRequired })),
  }),
  tab: PropTypes.string.isRequired,
  // (element) => void: a callback ref for the line's right end (the tab's tools, portalled there).
  toolsRef: PropTypes.func,
};

const VIEW_LABELS = { apps: 'Apps', files: 'Files', api: 'API', mcp: 'MCP', data: 'Data', traces: 'Traces', jobs: 'Jobs', finops: 'FinOps', quality: 'Quality', config: 'Configuration', mail: 'External Services', terminal: 'Terminal', git: 'Git', changes: 'Changes', activity: 'Activity', sessions: 'Sessions' };

export default function WorkspaceView({
  view: asked, onChange, projectId, projectName = null, serviceName = null, youId = null, onOpenSuggestion = null, onOpenHandoff = null, onAskAI = null, historyMerged = false,
  changesCount = 0, onOpenLogs = null, readOnly = false,
}) {
  // An older view (Configuration on its jobs) is shown as today's (Jobs), and the page is told so the link and the row agree.
  const view = normalizeView(asked);
  useEffect(() => { if (asked && view !== asked) { onChange(view); } }, [asked]); // eslint-disable-line react-hooks/exhaustive-deps
  const tab = view?.tab || null;
  // The links into another tab only when the person's plan shows it.
  const shows = usePlanTab();
  useEffect(() => { if (HISTORY_IDS.includes(tab)) { rememberHistorySub(tab); } }, [tab]);
  // The project whose Terminal tab was opened: its shells are kept (hidden) while the person is elsewhere in it.
  const [terminalFor, setTerminalFor] = useState(null);
  useEffect(() => { if (tab === 'terminal' && projectId) { setTerminalFor(projectId); } }, [tab, projectId]);
  // The title's right end (a tab's tools go there) and the page of the tab's own shown, its last crumb (Quality's).
  const [titleTools, setTitleTools] = useState(null);
  // The slot after the tab's name in the title (Quality's section picker).
  const [titleCrumb, setTitleCrumb] = useState(null);
  const [subPage, setSubPage] = useState(null);
  const terminalShown = tab === 'terminal' && Boolean(projectId);
  const keepTerminal = Boolean(projectId) && shows('terminal') && (terminalShown || terminalFor === projectId);
  // Always first and always the same element, so React keeps it (and its shells) across tab changes.
  const terminal = keepTerminal ? (
    <section aria-label="Terminal" className="workspace-view is-terminal" data-testid="workspace-view-terminal" hidden={!terminalShown}>
      <WorkspaceTitle compact projectName={projectName} tab="terminal" />
      <div className="workspace-view-body is-terminal">
        <TerminalTabView projectId={projectId} shown={terminalShown} />
      </div>
    </section>
  ) : null;
  if (!view || !projectId || terminalShown) { return <>{terminal}{null}</>; }
  const inHistory = historyMerged && HISTORY_IDS.includes(view.tab);
  const close = () => onChange(null);
  const sub = view.sub === 'timeline' || view.sub === 'versions' ? view.sub : 'person';
  // The Files tab on one file: "<Project> / Files / <path>", Files back to the table.
  const filesSub = view.tab === 'files' && view.focus?.path
    ? { title: view.focus.path, hint: view.focus.path, onUp: () => onChange({ tab: 'files', focus: null }) }
    : null;
  const chooseSub = (next) => {
    rememberChangesSub(next);
    onChange({ tab: 'changes', sub: next, focus: null });
  };
  return (
    <>
    {terminal}
    <section
      aria-label={inHistory ? `History: ${VIEW_LABELS[view.tab]}` : VIEW_LABELS[view.tab] || 'Sessions'}
      className={`workspace-view is-${view.tab}${inHistory ? ` is-history-${view.tab}` : ''}`}
      data-testid={`workspace-view-${view.tab}`}
    >
      <WorkspaceTitle
        crumbRef={view.tab === 'quality' ? setTitleCrumb : null}
        projectName={projectName}
        sub={view.tab === 'quality' ? subPage : filesSub}
        tab={view.tab}
        toolsRef={setTitleTools}
      />
      {inHistory && (
        <div aria-label="History" className="workspace-subtabs workspace-history-subs" data-testid="history-subs" role="tablist">
          {HISTORY_SUBS.map((s) => (
            <button
              aria-selected={view.tab === s.id}
              className={`workspace-subtab${view.tab === s.id ? ' is-on' : ''}`}
              data-testid={`history-sub-${s.id}`}
              key={s.id}
              onClick={() => { if (view.tab !== s.id) { onChange(viewFromHistory(s.id)); } }}
              role="tab"
              type="button"
            >
              {s.label}
              {s.id === 'changes' && changesCount > 0 && <span aria-label={`${changesCount} new`} className="workspace-tab-count">{changesCount > 99 ? '99+' : changesCount}</span>}
            </button>
          ))}
        </div>
      )}
      {view.tab === 'changes' && (
        <>
          <div aria-label="Changes" className="workspace-subtabs" role="tablist">
            {CHANGES_SUBS.map((s) => (
              <button
                aria-selected={sub === s.id}
                className={`workspace-subtab${sub === s.id ? ' is-on' : ''}`}
                data-testid={`changes-sub-${s.id}`}
                key={s.id}
                onClick={() => chooseSub(s.id)}
                role="tab"
                type="button"
              >
                {s.label}
              </button>
            ))}
          </div>
          <div className={`workspace-view-body is-${sub}`} role="tabpanel">
            {sub === 'person' && <ChangesPanel embedded focus={view.focus?.shas?.length ? view.focus : null} isOpen onClose={close} projectId={projectId} />}
            {sub === 'timeline' && <HistoryPanel embedded focusSha={view.focus?.sha || null} isOpen onClose={close} projectId={projectId} serviceName={serviceName} />}
            {sub === 'versions' && <VersionsPanel projectId={projectId} />}
          </div>
        </>
      )}
      {view.tab === 'activity' && (
        <div className="workspace-view-body is-activity">
          <ActivityPanel
            embedded
            isOpen
            focus={view.focus || null}
            onClose={close}
            onOpenChanges={(link) => onChange({ tab: 'changes', sub: 'person', focus: { shas: link.shas, runId: link.runId } })}
            onOpenHandoff={onOpenHandoff}
            onOpenSuggestion={(id) => onOpenSuggestion?.(id)}
            projectId={projectId}
            youId={youId}
          />
        </div>
      )}
      {view.tab === 'apps' && (
        <div className="workspace-view-body is-apps">
          <AppsPanel focus={view.focus || null} onOpenLogs={onOpenLogs} onOpenView={onChange} projectId={projectId} readOnly={readOnly} shows={shows} />
        </div>
      )}
      {view.tab === 'files' && (
        <div className="workspace-view-body is-files">
          <FilesPanel canEdit={!readOnly} canOpenCode={shows('code')} focus={view.focus || null} onOpenView={onChange} projectId={projectId} />
        </div>
      )}
      {view.tab === 'api' && (
        <div className="workspace-view-body is-api">
          <ApiPanel focus={view.focus || null} onOpenData={shows('data') ? () => onChange({ tab: 'data' }) : null} projectId={projectId} />
        </div>
      )}
      {view.tab === 'mcp' && (
        <div className="workspace-view-body is-mcp">
          <McpPanel focus={view.focus || null} projectId={projectId} />
        </div>
      )}
      {view.tab === 'data' && (
        <div className="workspace-view-body is-data">
          <DataPanel focus={view.focus || null} key={view.focus?.store || (view.focus?.view ? `${view.focus.view}:${view.focus.point || ''}` : 'data')} onAskAI={onAskAI} onLeave={close} projectId={projectId} projectName={projectName || projectId} serviceName={serviceName} />
        </div>
      )}
      {view.tab === 'traces' && (
        <div className="workspace-view-body is-traces">
          <TracesPanel focus={view.focus || null} onAskAI={onAskAI} onOpenLogs={onOpenLogs} onOpenView={onChange} projectId={projectId} shows={shows} />
        </div>
      )}
      {view.tab === 'quality' && (
        <div className="workspace-view-body is-quality">
          <QualityPanel focus={view.focus || null} onAskAI={onAskAI} onOpenView={onChange} onSubPage={setSubPage} projectId={projectId} titleCrumb={titleCrumb} titleTools={titleTools} />
        </div>
      )}
      {view.tab === 'config' && (
        <div className="workspace-view-body is-config">
          <ConfigPanel focus={view.focus || null} onOpenJobs={shows('jobs') ? () => onChange({ tab: 'jobs', focus: null }) : null} projectId={projectId} />
        </div>
      )}
      {view.tab === 'jobs' && (
        <div className="workspace-view-body is-jobs">
          <JobsPanel focus={view.focus || null} onAskAI={onAskAI} projectId={projectId} />
        </div>
      )}
      {view.tab === 'finops' && (
        <div className="workspace-view-body is-finops">
          <FinOpsPanel focus={view.focus || null} projectId={projectId} />
        </div>
      )}
      {view.tab === 'mail' && (
        <div className="workspace-view-body is-mail">
          <ExternalServicesPanel focus={view.focus || null} projectId={projectId} />
        </div>
      )}
      {view.tab === 'git' && (
        <div className="workspace-view-body is-git">
          <GitTab focus={view.focus || null} projectId={projectId} readOnly={readOnly} />
        </div>
      )}
      {view.tab === 'sessions' && (
        <div className="workspace-view-body is-sessions">
          <ProjectSessions focus={view.focus || null} projectId={projectId} />
        </div>
      )}
    </section>
    </>
  );
}

WorkspaceView.propTypes = {
  changesCount: PropTypes.number,
  historyMerged: PropTypes.bool,
  // (prompt, { prefill }) => void: the project chat (App's askAIInProjectChat); the Data tab prefills it.
  onAskAI: PropTypes.func,
  onChange: PropTypes.func.isRequired,
  onOpenHandoff: PropTypes.func,
  // (service, { at }) => void: the project's Logs view on that service, around that time (Traces → Open logs).
  onOpenLogs: PropTypes.func,
  onOpenSuggestion: PropTypes.func,
  projectId: PropTypes.string,
  projectName: PropTypes.string,
  // A viewer of the project: the Git tab without its write actions.
  readOnly: PropTypes.bool,
  serviceName: PropTypes.string,
  view: PropTypes.shape({ tab: PropTypes.string, sub: PropTypes.string, focus: PropTypes.object }),
  youId: PropTypes.string,
};
